/**
 * MediaLightbox — Full-screen media viewer with swipe navigation
 * v142.3.0: Used by DetectScreen for Doctor's Findings
 * 
 * Props:
 *   items: [{ url, name, type, label, summary }]
 *   startIndex: number (which item to show first)
 *   onClose: () => void
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { OverlayPortal } from './ModalPortal'
import { X, ChevronLeft, ChevronRight, FileText, Film } from 'lucide-react'
import { useI18n } from '../i18n'

const NAVY = '#15354F'

export default function MediaLightbox({ items = [], startIndex = 0, onClose }) {
  const { t } = useI18n()
  const [currentIndex, setCurrentIndex] = useState(startIndex)
  const touchStartX = useRef(null)
  const touchStartY = useRef(null)

  const item = items[currentIndex]
  // FHOOK: every hook below runs on every render (rules of hooks); the `!item` early return moved
  //   under the last hook. The two effects do nothing while there is no item, exactly as before,
  //   when the component rendered null before reaching them.
  const hasItem = !!item
  const canPrev = currentIndex > 0
  const canNext = currentIndex < items.length - 1

  const goNext = useCallback(() => {
    if (canNext) setCurrentIndex(i => i + 1)
  }, [canNext])

  const goPrev = useCallback(() => {
    if (canPrev) setCurrentIndex(i => i - 1)
  }, [canPrev])

  // Keyboard navigation
  useEffect(() => {
    if (!hasItem) return
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') goNext()
      if (e.key === 'ArrowLeft') goPrev()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [hasItem, onClose, goNext, goPrev])

  // Prevent body scroll while lightbox is open
  useEffect(() => {
    if (!hasItem) return
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [hasItem])

  if (!item) return null

  const isImage = item.type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(item.name || '')
  const isVideo = item.type?.startsWith('video/') || /\.(mp4|mov|webm|avi)$/i.test(item.name || '')
  const displayUrl = item.url || item.preview || item.data

  // Touch swipe handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
  }

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return
    const deltaX = e.changedTouches[0].clientX - touchStartX.current
    const deltaY = e.changedTouches[0].clientY - touchStartY.current
    touchStartX.current = null
    touchStartY.current = null

    // Only swipe if horizontal movement > vertical and > 50px threshold
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX < 0) goNext()
      else goPrev()
    }
  }

  return (
    <OverlayPortal>
    <div
      className="fixed inset-0 z-[200] bg-black/95 flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0">
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-[14px] truncate">
            {item.displayName || item.name || item.label || t('common.itemNum', { num: currentIndex + 1 })}
          </p>
          {items.length > 1 && (
            <p className="text-white/40 text-[12px]">{currentIndex + 1} of {items.length}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:bg-white/20 ml-3 flex-shrink-0"
        >
          <X size={20} className="text-white" />
        </button>
      </div>

      {/* Media area */}
      <div className="flex-1 flex items-center justify-center px-4 py-2 relative overflow-hidden">
        {/* Prev arrow (desktop) */}
        {canPrev && (
          <button
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:bg-white/20 z-10 hidden sm:flex"
          >
            <ChevronLeft size={24} className="text-white" />
          </button>
        )}

        {/* Content */}
        {isImage && displayUrl ? (
          <img
            src={displayUrl}
            alt={item.displayName || item.name || ''}
            className="max-w-full max-h-full object-contain rounded-lg select-none"
            draggable={false}
          />
        ) : isVideo && displayUrl ? (
          <video
            src={displayUrl}
            controls
            autoPlay
            playsInline
            className="max-w-full max-h-full rounded-lg"
          >
            {t('mediaLightbox.videoNotSupported')}
          </video>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center">
              <FileText size={36} className="text-white/60" />
            </div>
            <p className="text-white/60 text-[13px] text-center">
              {item.displayName || item.name || 'Document'}
            </p>
            {displayUrl && (
              <a
                href={displayUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-[#46A8F9] text-white text-[13px] font-semibold rounded-full active:opacity-80"
              >
                {t('mediaLightbox.openDocument')}
              </a>
            )}
          </div>
        )}

        {/* Next arrow (desktop) */}
        {canNext && (
          <button
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:bg-white/20 z-10 hidden sm:flex"
          >
            <ChevronRight size={24} className="text-white" />
          </button>
        )}
      </div>

      {/* Bottom info */}
      <div className="px-4 pb-6 pt-2 flex-shrink-0">
        {item.label && (
          <span className="text-[#46A8F9] text-[12px] font-semibold">{item.label}</span>
        )}
        {item.date && (
          <p className="text-white/40 text-[11px] mt-0.5">{item.date}</p>
        )}
        {item.summary && typeof item.summary === 'string' && (
          <p className="text-white/60 text-[12px] mt-1.5 line-clamp-3">{item.summary}</p>
        )}
        {/* Dot indicators for multiple items */}
        {items.length > 1 && items.length <= 10 && (
          <div className="flex items-center justify-center gap-1.5 mt-3">
            {items.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`rounded-full transition-all ${
                  idx === currentIndex
                    ? 'w-2.5 h-2.5 bg-[#46A8F9]'
                    : 'w-1.5 h-1.5 bg-white/30'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
    </OverlayPortal>
  )
}
